import { ContentItemBox } from 'ContentItemBox';
import { type ReactNode, type FC, memo, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useForceExpanded } from 'StudentDashboardSlice';
import './locales/courseItemBox/courseItemBox-en.json';

type CourseProgressInfo = {
    status: 'completed' | 'inProgress' | 'notStarted' | 'nextUp';
    totalLessons?: number;
    completedLessons?: number;
    hasStarted?: boolean;
};

type Props =
    | {
          title: string;
          src: string;
          courseId: string;
          progressInfo: CourseProgressInfo | null;
          showSubtitle?: boolean;
          onPress: () => void;
          availableOffline?: boolean;
          padding?: '2.5' | '3.5';
          titleSize?: 'base' | 'xs' | 'sm';
          forceExpanded?: boolean;
          hideEndIcon?: boolean;
      }
    | {
          title: string;
          src: string;
          courseId: string;
          progressInfo: CourseProgressInfo | null;
          showSubtitle?: boolean;
          expandedContent: ReactNode;
          availableOffline?: boolean;
          padding?: '2.5' | '3.5';
          titleSize?: 'base' | 'xs' | 'sm';
          forceExpanded?: boolean;
          hideEndIcon?: boolean;
      };

const colorStatusMap = {
    completed: 'text-coral',
    inProgress: 'text-purple',
    notStarted: 'text-slate-grey',
    nextUp: 'text-green',
} as const;

const Icon = memo(({ status }: { status: keyof typeof colorStatusMap }) => (
    <FontAwesomeIcon
        icon={status === 'completed' ? faCheck : faAngleRight}
        // Don't flip checkmarks in rtl
        className={twMerge('text-base', status !== 'completed' ? 'rtl:scale-x-[-1]' : '', colorStatusMap[status])}
    />
));

export const CourseItemBox: FC<Props> = memo(
    ({
        title,
        progressInfo,
        showSubtitle = true,
        availableOffline = false,
        padding = '3.5',
        titleSize = 'base',
        courseId,
        hideEndIcon = false,
        ...rest
    }) => {
        const { t } = useTranslation('back_royal', { keyPrefix: 'courseItemBox.courseItemBox' });

        const subtitle = useMemo(() => {
            if (!progressInfo || !showSubtitle) return undefined;

            const { totalLessons, completedLessons, hasStarted, status } = progressInfo;

            if (!totalLessons || completedLessons === null || !hasStarted) return undefined;

            if (completedLessons === totalLessons || status === 'completed') return undefined;

            return t('completedLessons', {
                completedLessons,
                totalLessons: progressInfo.totalLessons,
            });
        }, [progressInfo, showSubtitle, t]);

        const forceExpanded = useForceExpanded(courseId);

        return (
            <ContentItemBox
                title={title}
                imageSize={10}
                desktopImageSize={7.5}
                height={18}
                desktopHeight={14.5}
                padding={padding}
                titleSize={titleSize}
                fullWidth
                endIcon={hideEndIcon ? undefined : <Icon status={progressInfo?.status ?? 'notStarted'} />}
                subtitle={subtitle}
                availableOffline={availableOffline}
                itemId={courseId}
                forceExpanded={forceExpanded}
                {...rest}
            />
        );
    },
);
